"use client";

import { useEffect } from "react";
import Script from "next/script";
import { useConsent } from "./consent-store";
import type { SeoTrackers } from "@/lib/seo-settings";

/**
 * Consent-gated analytics & marketing loader (LGPD Art. 7/8 + Google Consent Mode v2).
 *
 * 1. Emits Google Consent Mode v2 defaults ('denied' for ads & analytics) on first paint.
 * 2. On user decision, updates Consent Mode via `gtag('consent', 'update', ...)` and pushes
 *    `consent_update` to window.dataLayer.
 * 3. Injects approved tracking scripts:
 *    - GTM (which respects Consent Mode v2 natively for GA4 and Google Ads tags)
 *    - Direct fallbacks (GA4, Google Ads, Meta Pixel, Clarity, Hotjar, LinkedIn, TikTok)
 */
export function TrackingScripts({ trackers }: { trackers: SeoTrackers }) {
  const { preferences, hasDecided, hydrated } = useConsent();

  // Consent Mode v2: push update whenever the user grants or modifies consent.
  useEffect(() => {
    if (!hydrated || !hasDecided || typeof window === "undefined") return;
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    function gtag(...args: any[]) {
      w.dataLayer.push(args);
    }
    gtag("consent", "update", {
      analytics_storage: preferences.analytics ? "granted" : "denied",
      ad_storage: preferences.marketing ? "granted" : "denied",
      ad_user_data: preferences.marketing ? "granted" : "denied",
      ad_personalization: preferences.marketing ? "granted" : "denied",
    });
    w.dataLayer.push({
      event: "consent_update",
      analytics_consent: preferences.analytics,
      marketing_consent: preferences.marketing,
    });
  }, [hydrated, hasDecided, preferences.analytics, preferences.marketing]);

  const t = trackers;
  const hasGtm = !!t.gtmId;
  const hasGoogleTracker = hasGtm || !!t.ga4Id || !!t.googleAdsId;

  const consentModeDefaultScript = hasGoogleTracker ? (
    <Script key="google-consent-mode" id="google-consent-mode" strategy="beforeInteractive">
      {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{'analytics_storage':'denied','ad_storage':'denied','ad_user_data':'denied','ad_personalization':'denied','functionality_storage':'granted','security_storage':'granted','wait_for_update':500});`}
    </Script>
  ) : null;

  if (!hydrated || !hasDecided) {
    return consentModeDefaultScript;
  }

  const out: React.ReactNode[] = [];
  if (consentModeDefaultScript) {
    out.push(consentModeDefaultScript);
  }

  // --- GTM (handles analytics + marketing inside its container with Consent Mode v2) ---
  if (hasGtm && (preferences.analytics || preferences.marketing)) {
    out.push(
      <Script key="gtm" id="gtm" strategy="afterInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${t.gtmId}');`}
      </Script>,
    );
  }

  // --- analytics ---
  if (preferences.analytics) {
    if (t.ga4Id && !hasGtm) {
      out.push(
        <Script key="ga4-src" src={`https://www.googletagmanager.com/gtag/js?id=${t.ga4Id}`} strategy="afterInteractive" />,
        <Script key="ga4" id="ga4" strategy="afterInteractive">
          {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${t.ga4Id}');`}
        </Script>,
      );
    }
    if (t.clarityId) {
      out.push(
        <Script key="clarity" id="ms-clarity" strategy="afterInteractive">
          {`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${t.clarityId}");`}
        </Script>,
      );
    }
    if (t.hotjarId) {
      out.push(
        <Script key="hotjar" id="hotjar" strategy="afterInteractive">
          {`(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${t.hotjarId},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`}
        </Script>,
      );
    }
  }

  // --- marketing ---
  if (preferences.marketing && !hasGtm) {
    if (t.metaPixelId) {
      out.push(
        <Script key="meta-pixel" id="meta-pixel" strategy="afterInteractive">
          {`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${t.metaPixelId}');fbq('track','PageView');`}
        </Script>,
      );
    }
    if (t.googleAdsId) {
      out.push(
        <Script key="gads-src" src={`https://www.googletagmanager.com/gtag/js?id=${t.googleAdsId}`} strategy="afterInteractive" />,
        <Script key="gads" id="google-ads" strategy="afterInteractive">
          {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${t.googleAdsId}');`}
        </Script>,
      );
    }
    if (t.linkedinPartnerId) {
      out.push(
        <Script key="linkedin" id="linkedin-insight" strategy="afterInteractive">
          {`_linkedin_partner_id="${t.linkedinPartnerId}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];var b=document.createElement("script");b.type="text/javascript";b.async=true;b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";s.parentNode.insertBefore(b,s);})(window.lintrk);`}
        </Script>,
      );
    }
    if (t.tiktokPixelId) {
      out.push(
        <Script key="tiktok" id="tiktok-pixel" strategy="afterInteractive">
          {`!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var o=d.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${t.tiktokPixelId}');ttq.page();}(window,document,'ttq');`}
        </Script>,
      );
    }
  }

  return <>{out}</>;
}
